import AttachmentPanel, {
  AttachmentMenu,
  ModelNotices,
} from "./attachments/AttachmentPanel";
import useAttachments from "./attachments/useAttachments";
import { setOriginalLoader } from "./attachments/localFiles";
import useUploadGate from "./attachments/useUploadGate";
import { isUploading, toPageMessages } from "./attachments/attachmentState";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../firebase/AuthContext";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import { useLanguageSync } from "../../../hooks/useLanguageSync";
import {
  PdfPanelContext,
  PdfPanelProvider,
  PdfSidePanel,
} from "../../../components/MessageWithTableRenderer";
import { createAttachmentClient } from "./api/attachments";
import {
  ROOT_AGENT_API,
  createChatSession,
  streamChatMessage,
} from "./api/chat";
import { getApiUrl } from "../../../config/api";
import AssistantChatInput from "./components/AssistantChatInput";
import AssistantSettingsPanel, {
  settingsSummary,
} from "./components/AssistantSettingsPanel";
import SampleQuestions from "./components/SampleQuestions";
import MessageBubble from "./components/MessageBubble";
import ProductMessageActions from "./components/ProductMessageActions";

function randomId() {
  return Math.random().toString(36).slice(2, 10);
}

// True when text breaks off mid-sentence — no closing punctuation and no line
// break. The video path streams its AI blurb as fragments like "這支" and
// "影片的「…」章節", which end this way; a self-contained block (a table, the
// "no video, using text instead" notice, a finished paragraph) does not.
const ENDS_MID_SENTENCE = /[^\s。．.!?！？:：\n]$/;

// Appends one content block to the answer being assembled.
//
// Everything lands in ONE string so MessageRenderer parses it in a single pass
// (spec §4: parsing blocks separately loses the trailing `Source:` links). The
// only question is whether a block continues the previous text or starts a new
// paragraph. The event's own `partial` flag cannot answer that — the product
// path marks a COMPLETE table `partial: true`, where it means only "more events
// follow" — so a fragment is identified by how the text before it ends.
function appendBlock(existing, block) {
  if (!existing) return block;
  const continuesSentence =
    ENDS_MID_SENTENCE.test(existing) && !block.startsWith("\n");
  return continuesSentence ? existing + block : `${existing}\n\n${block}`;
}

// ── Conversations are recorded by the Root Agent ────────────────────────────
// The page never saves: the Root Agent records every turn it answers into
// History (conversations/{uid}) as the answer streams, a closed tab included.

// Root's capability answer decides whether attachments are offered.
function TgProductAssistant({
  attachmentClient: injectedAttachmentClient = null,
}) {
  const { currentUser } = useAuth();
  const attachmentClient = useMemo(() => {
    if (!currentUser) return null;
    return (
      injectedAttachmentClient ||
      createAttachmentClient({
        baseUrl: ROOT_AGENT_API,
        getToken: () => currentUser.getIdToken(),
      })
    );
  }, [currentUser, injectedAttachmentClient]);
  const { language, setLanguage } = useLanguageSync();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [historicalFiles, setHistoricalFiles] = useState([]);
  const attachments = useAttachments(
    attachmentClient,
    sessionId,
    historicalFiles,
  );
  useEffect(() => {
    setOriginalLoader(
      sessionId,
      attachmentClient && sessionId
        ? (id) => attachments.controller.loadOriginal(id)
        : null,
    );
    return () => setOriginalLoader(null, null);
  }, [attachmentClient, sessionId, attachments.controller]);
  const [input, setInput] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  // Deliberately not reset after a send — someone looking for videos usually
  // asks several questions in a row.
  const [includeVideo, setIncludeVideo] = useState(false);
  // Answer settings. Sent with every message rather than bound to the session
  // (as the FAQ tab does), so changing one applies to the next question instead
  // of requiring a new chat. Defaults match the backend's own.
  const [model, setModel] = useState("gemini-3.5-flash-lite");
  const [thinking, setThinking] = useState("balanced");
  const [verbosity, setVerbosity] = useState("medium");
  const scrollAnchorRef = useRef(null);
  const textareaRef = useRef(null);
  const busyRef = useRef(false);
  const sessionGeneration = useRef(0);
  const [sessionError, setSessionError] = useState(false);
  // The right of the page shows one thing at a time: the document viewer or the
  // agent settings. Settings start closed and close on send; a document opening
  // closes them, and opening them closes the documents.
  const pdfPanel = useContext(PdfPanelContext);
  const { openPdfs } = pdfPanel;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const docsOpen = openPdfs.length > 0;
  useEffect(() => {
    if (docsOpen) setSettingsOpen(false);
  }, [docsOpen]);
  const toggleSettings = () => {
    if (!settingsOpen) pdfPanel.closeAll();
    setSettingsOpen(!settingsOpen);
  };

  const userId = currentUser?.email ?? "anonymous";

  const startNewChat = async () => {
    if (busyRef.current) return;
    const generation = ++sessionGeneration.current;
    setSessionError(false);
    setSessionId(null);
    setMessages([]);
    setHistoricalFiles([]);
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const nextSessionId = await createChatSession(token, userId);
      if (generation === sessionGeneration.current) setSessionId(nextSessionId);
    } catch (err) {
      console.error("[TgProductAssistant] Failed to create chat session:", err);
      if (generation === sessionGeneration.current) {
        setSessionId(null);
        setSessionError(true);
      }
    }
  };

  // Reopens a conversation from History: its saved messages, and the agent's
  // session under the same id, so the agent still has the earlier turns as
  // context when it kept them. Falls back to a new chat if it cannot load.
  const continueChat = async (savedSessionId) => {
    const generation = ++sessionGeneration.current;
    setSessionError(false);
    setSessionId(null);
    setMessages([]);
    setHistoricalFiles([]);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(
        `${getApiUrl()}/api/v2/history/${savedSessionId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      await createChatSession(token, userId, savedSessionId);
      if (generation === sessionGeneration.current) {
        setMessages(toPageMessages(data.messages || []));
        setHistoricalFiles(
          (data.messages || []).flatMap((message) =>
            message.role === "user" ? message.files || [] : [],
          ),
        );
        setSessionId(savedSessionId);
      }
    } catch (err) {
      console.error("[TgProductAssistant] Failed to continue the chat:", err);
      if (generation === sessionGeneration.current) startNewChat();
    }
  };

  useEffect(() => {
    busyRef.current = false;
    setIsBusy(false);
    // History hands a conversation over through this key (see openInChat).
    const handedOver = currentUser && localStorage.getItem("sessionId");
    if (handedOver) {
      localStorage.removeItem("sessionId");
      continueChat(handedOver);
    } else {
      startNewChat();
    }
    return () => {
      sessionGeneration.current += 1;
    };
    // One session on mount; afterwards a new chat is user-triggered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!isBusy && sessionId) textareaRef.current?.focus();
  }, [isBusy, sessionId]);

  // Retrying appends a new turn: the backend has no conversation rewind API.
  const handleSend = async (retryQuestion, retryIds) => {
    const question = (retryQuestion ?? input).trim();
    const ids = retryIds || (attachmentClient ? [...attachments.selected] : []);
    if (
      (!question && !ids.length) ||
      busyRef.current ||
      !sessionId ||
      !currentUser
    )
      return;
    // A file that is still uploading or being processed is not part of the question yet.
    if (
      attachmentClient &&
      attachments.controller.getSnapshot().pending.some(isUploading)
    )
      return;

    // Sending closes the agent settings; an open document stays.
    setSettingsOpen(false);
    if (!ids.length && retryQuestion == null) setInput("");
    busyRef.current = true;
    setIsBusy(true);
    const generation = sessionGeneration.current;

    let files = [];
    if (ids.length > 0) {
      try {
        files = await attachments.controller.prepare(model, includeVideo, ids);
      } catch (error) {
        if (generation === sessionGeneration.current) {
          attachments.controller.patch({
            error: error.detail || { message: error.message },
          });
          busyRef.current = false;
          setIsBusy(false);
        }
        return;
      }
      if (generation !== sessionGeneration.current) return;
      if (retryQuestion == null) setInput("");
    }
    const userMessage = { id: randomId(), role: "user", text: question, files };
    const assistantId = randomId();
    const startedAt = Date.now();

    setMessages((prev) => [
      ...prev,
      userMessage,
      {
        id: assistantId,
        role: "assistant",
        text: "",
        status: "loading",
        stage: "routing",
        startedAt,
        question,
        files,
        attachmentTurn: files.length > 0,
        userMessageId: userMessage.id,
      },
    ]);

    const updateAssistant = (patch) => {
      if (generation !== sessionGeneration.current) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? {
                ...m,
                ...patch,
                status:
                  files.length > 0 && m.status === "error"
                    ? "error"
                    : patch.status || m.status,
              }
            : m,
        ),
      );
    };

    try {
      const token = await currentUser.getIdToken();

      await streamChatMessage(
        token,
        userId,
        sessionId,
        question,
        {
          onStageChange: (stage) =>
            updateAssistant({
              stage,
              ...(files.length > 0 && stage === "error"
                ? { status: "error" }
                : {}),
            }),
          onAttachment: (patch) => updateAssistant(patch),
          // Everything accumulates into ONE string so MessageRenderer parses it in
          // a single pass — parsing blocks separately would drop the `Source:`
          // links that only appear at the end (spec §4).
          //
          // How a block joins depends on what preceded it. The video path streams
          // its AI blurb as a run of `partial: true` fragments with no line breaks
          // of their own ("這支" / "影片的「…」章節" / "直接對應…"), so those
          // concatenate directly; a blank line between each would break one
          // sentence into a dozen paragraphs. Everything else — the product
          // path's table then analysis, the video path's markdown list after the
          // blurb — is a self-contained block that starts a new paragraph.
          onBlock: (block) => {
            if (generation !== sessionGeneration.current) return;
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== assistantId) return m;
                return {
                  ...m,
                  text: appendBlock(m.text, block),
                  status:
                    files.length > 0 && m.status === "error"
                      ? "error"
                      : "streaming",
                };
              }),
            );
          },
          // FAQ-path answers only (faq_flow.py's terminal stateDelta.sources) — the
          // [1], [2]... markers already inside the answer text refer to this list.
          // product_direct answers never call this.
          onSources: (sources) => {
            updateAssistant({ sources });
          },
          // Structured video cards. The markdown text lists the same videos, so
          // once these arrive the text list is redundant — see VideoCards.
          onVideos: (videos) => {
            updateAssistant({ videos });
          },
          // Which backend answered. Recorded per message because the routing is
          // per-question: a complex question is sent to GPT even while the picker
          // sits on Gemini, so the selected model cannot tell us where an answer
          // came from — only this can.
          onWorker: (worker) => {
            updateAssistant({ worker });
          },
          // The stream closing IS the end of the turn — there is no turn_complete
          // event to wait for (spec §3.1).
          onDone: () => {
            updateAssistant({
              status: "done",
              elapsedMs: Date.now() - startedAt,
            });
          },
          onError: (error) => {
            updateAssistant({
              status: "error",
              errorText: error.message,
              errorDetail: error.detail,
              elapsedMs: Date.now() - startedAt,
            });
          },
        },
        // Answer language. The toggle wins over the question's own script, so asking
        // in English with the toggle on Chinese answers in Chinese.
        language,
        includeVideo,
        { model, thinking, verbosity, attachmentIds: ids },
      );
    } catch (error) {
      updateAssistant({
        status: "error",
        errorText: error.message,
        elapsedMs: Date.now() - startedAt,
      });
    } finally {
      if (generation === sessionGeneration.current) {
        busyRef.current = false;
        setIsBusy(false);
      }
    }
  };

  const uploadFiles = (files) => {
    files.forEach((file) => attachments.controller.upload(file));
  };
  // Picking, pasting and dropping all start here: the upload notice appears first unless the user turned it off.
  const uploadGate = useUploadGate({
    capability: attachments.capability,
    language,
    upload: uploadFiles,
  });
  // Until the capability answers (a cold server can take seconds) show a busy placeholder instead of nothing.
  const connecting =
    attachmentClient && !attachments.capability && attachments.checking;
  const attachmentInput = connecting
    ? {
        entry: <AttachmentMenu loading language={language} />,
        selectedCount: 0,
        hasSelection: false,
        blocked: false,
      }
    : attachmentClient && attachments.capability?.enabled
      ? {
          entry: (
            <>
              <AttachmentMenu
                disabled={isBusy || attachments.checking}
                language={language}
                onPick={() => uploadGate.request([])}
                onInfo={uploadGate.openInfo}
              />
              {uploadGate.element}
            </>
          ),
          onFiles: uploadGate.request,
          selectedCount: attachments.selected.length,
          hasSelection: attachments.selected.length > 0,
          uploading: attachments.pending.some(isUploading),
          blocked:
            attachments.pending.some(isUploading) ||
            (attachments.selected.length > 0 &&
              (!!attachments.controller.validation(model, includeVideo) ||
                attachments.checking)),
        }
      : null;

  if (!currentUser) return null;

  const footerNote =
    language === "zh"
      ? "新版 AI 聊天・測試中 — AI 生成的答案，仍需人工確認"
      : "New AI Chat · In testing — AI-generated answers may need human review";

  const summary = settingsSummary(model, thinking, language);
  const settingsPanel = settingsOpen && (
    <AssistantSettingsPanel
      language={language}
      model={model}
      setModel={setModel}
      thinking={thinking}
      setThinking={setThinking}
      verbosity={verbosity}
      setVerbosity={setVerbosity}
      onClose={() => setSettingsOpen(false)}
    />
  );
  // One chat box for both screens; the conversation's also has New Chat.
  const composer = (variant) => (
    <>
      <AssistantChatInput
        input={input}
        setInput={setInput}
        isLoading={isBusy}
        language={language}
        onSubmit={() => handleSend()}
        onNewChat={variant === "compact" ? startNewChat : undefined}
        newChatDisabled={isBusy}
        disabled={!sessionId}
        attachmentInput={attachmentInput}
        textareaRef={textareaRef}
        variant={variant}
        tray={
          attachmentClient && (
            <AttachmentPanel
              attachments={attachments}
              model={model}
              includeVideo={includeVideo}
              busy={isBusy}
              language={language}
            />
          )
        }
        includeVideo={includeVideo}
        onIncludeVideoChange={setIncludeVideo}
        settings={{
          open: settingsOpen,
          onToggle: toggleSettings,
          ...summary,
        }}
      />
      <ModelNotices
        capability={attachments.capability}
        model={model}
        language={language}
      />
      <p className="mt-2 text-center text-xs text-black/70 dark:text-white/70 italic">
        {footerNote}
      </p>
    </>
  );

  // --- Empty state (no messages yet) ---
  if (messages.length === 0) {
    return (
      <div className="flex min-h-screen bg-slate-100 dark:bg-dashboard-bg text-black font-sans">
        <Sidebar />
        {!settingsOpen && (
          <LanguageSwitcher language={language} setLanguage={setLanguage} />
        )}

        <div className="flex-1 min-w-0 flex flex-col items-center justify-center overflow-y-auto py-6 md:py-8 px-4">
          <span className="mb-2 md:mb-3 inline-flex items-center rounded-full border border-orange-500/30 bg-orange-500/10 px-3 py-1 text-xs font-semibold text-orange-700 dark:text-orange-300">
            {language === "zh"
              ? "新版 AI 聊天・測試中"
              : "New AI Chat · In testing"}
          </span>
          <h2 className="text-black dark:text-white text-3xl md:text-4xl font-bold mb-2 md:mb-4 text-center">
            {language === "zh"
              ? "TGlobal 產品助理"
              : "TGlobal Product Assistant"}
          </h2>
          <p className="text-gray-700 dark:text-slate-300 text-sm mb-2 md:mb-4 text-center max-w-xl">
            {language === "zh"
              ? "這是新版 AI 聊天頁面，目前仍在測試中，未來將取代原有的 AI 聊天頁面。"
              : "This is our new AI Chat page. It is currently in testing and will replace the existing AI Chat page."}
          </p>
          <p className="text-gray-800 dark:text-slate-300 text-base md:text-lg mb-4 md:mb-8 text-center max-w-md">
            {language === "zh"
              ? "詢問產品規格、應用場景，或比較同一類別的產品。"
              : "Ask about product specs, applications, or compare products within a category."}
          </p>

          <div className="md:w-4/5 w-full m-2 mb-2 md:mb-8">
            <SampleQuestions
              setInput={setInput}
              setIncludeVideo={setIncludeVideo}
              language={language}
              onSelect={() => textareaRef.current?.focus()}
            />
          </div>
          {sessionError && (
            <p
              role="alert"
              className="text-sm text-red-600 dark:text-red-400 text-center"
            >
              {language === "zh" ? "無法建立對話。" : "Could not start a chat."}{" "}
              <button
                type="button"
                onClick={startNewChat}
                className="underline"
              >
                {language === "zh" ? "重試" : "Try again"}
              </button>
            </p>
          )}

          <div className="w-full md:w-3/5 my-3 md:my-5">{composer("full")}</div>
        </div>
        {settingsPanel}
      </div>
    );
  }

  // --- Conversation state (messages present) ---
  return (
    <div className="flex w-screen h-screen bg-slate-100 dark:bg-dashboard-bg text-black dark:text-white font-sans">
      <Sidebar />
      {!docsOpen && !settingsOpen && (
        <LanguageSwitcher language={language} setLanguage={setLanguage} />
      )}

      <div className="flex-1 h-full flex flex-col pt-16 md:pt-12 transition-all duration-300 min-w-0">
        <div className="w-full h-full flex flex-col items-center justify-center mx-auto px-4 md:px-0 w-[90%]">
          {/* Messages list */}
          <div className="w-full md:w-5/6 min-h-0 h-[70%] md:h-[65%] flex flex-col rounded-xl overflow-y-auto no-scrollbar gap-6 md:gap-12 px-2">
            {messages.map((message) => (
              <div key={message.id} className="w-full">
                <MessageBubble
                  message={message}
                  language={language}
                  attachmentFiles={attachments.files}
                  fileCardPrefix={message.userMessageId}
                  actions={
                    (message.status === "done" ||
                      message.status === "error") && (
                      <ProductMessageActions
                        message={message}
                        language={language}
                        currentUser={currentUser}
                        sessionId={sessionId}
                        isBusy={isBusy}
                        onRetry={
                          message.attachmentTurn
                            ? () =>
                                handleSend(
                                  message.question,
                                  (message.files || []).map((file) => file.id),
                                )
                            : handleSend
                        }
                      />
                    )
                  }
                />
              </div>
            ))}
            <div ref={scrollAnchorRef} />
          </div>

          {/* Input area */}
          <div className="w-full md:w-[70%] shrink-0 my-3 md:my-5">
            {composer("compact")}
          </div>
        </div>
      </div>

      {/* The right of the page: the agent settings or the documents, never both. */}
      {settingsPanel}
      {docsOpen && (
        <div className="w-[420px] xl:w-[600px] flex-shrink-0 h-full transition-all duration-300">
          <PdfSidePanel />
        </div>
      )}
    </div>
  );
}

// One PdfPanelProvider around both screens, so source-link PDFs open in the
// right-hand side panel (the same as PIMS Chat, AiChatPage.js) and the agent
// settings can share that side with them. Without the provider, ActionLinks
// falls back to rendering the PDF inline underneath the message.
function TgProductAssistantPage(props) {
  return (
    <PdfPanelProvider>
      <TgProductAssistant {...props} />
    </PdfPanelProvider>
  );
}

export default TgProductAssistantPage;
